import { Controller } from '@hotwired/stimulus'
import { gsap } from 'gsap'

export default class extends Controller {
  static values = {
    interval: { type: Number, default: 800 },
    duration: { type: Number, default: 0.7 },
    frontY: { type: Number, default: 67 },
    middleY: { type: Number, default: 23 },
    backY: { type: Number, default: 0 },
    frontX: { type: Number, default: -50 },
    middleX: { type: Number, default: -50 },
    backX: { type: Number, default: -50 },
    frontScale: { type: Number, default: 1 },
    middleScale: { type: Number, default: 0.93 },
    backScale: { type: Number, default: 0.75 },
    backOpacity: { type: Number, default: 0.7 },
  }

  static targets = ['item']

  connect() {
    this.currentIndex = 0
    this.itemCount = this.itemTargets.length
    this.isAnimating = false
    this.shouldRotate = false

    if (this.itemCount < 2 || this.itemCount !== this.positions.length) return

    this.applyPositions()

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (!prefersReducedMotion) {
      this.observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            this.startAutoRotate()
          } else {
            this.stopAutoRotate()
          }
        },
        { threshold: 0.1 },
      )
      this.observer.observe(this.element)
    }
  }

  disconnect() {
    this.stopAutoRotate()
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
    if (this.delayedCallHandle) {
      this.delayedCallHandle.kill()
      this.delayedCallHandle = null
    }
    gsap.killTweensOf(this.itemTargets)
  }

  get positions() {
    const front = {
      y: this.frontYValue,
      xPercent: this.frontXValue,
      scale: this.frontScaleValue,
      zIndex: 3,
      opacity: 1,
    }
    const middle = {
      y: this.middleYValue,
      xPercent: this.middleXValue,
      scale: this.middleScaleValue,
      zIndex: 2,
      opacity: 1,
    }
    const back = {
      y: this.backYValue,
      xPercent: this.backXValue,
      scale: this.backScaleValue,
      zIndex: 1,
      opacity: this.backOpacityValue,
    }

    if (this.itemCount === 2) return [front, back]
    return [front, middle, back]
  }

  startAutoRotate() {
    this.shouldRotate = true
    this.scheduleNextRotation()
  }

  scheduleNextRotation() {
    if (!this.shouldRotate) return
    if (this.rotationTimer) {
      this.rotationTimer.kill()
    }
    this.rotationTimer = gsap.delayedCall(
      this.intervalValue / 1000,
      () => this.rotate(),
    )
  }

  stopAutoRotate() {
    this.shouldRotate = false
    if (this.rotationTimer) {
      this.rotationTimer.kill()
      this.rotationTimer = null
    }
  }

  rotate() {
    if (this.isAnimating) return
    this.isAnimating = true

    const frontItemIndex = this.getFrontItemIndex()
    const frontItem = this.itemTargets[frontItemIndex]
    const exitDuration = this.durationValue * 0.5
    const enterDuration = this.durationValue * 0.6
    const backPosition = this.positions[this.positions.length - 1]

    gsap.to(frontItem, {
      y: this.frontYValue + 30,
      opacity: 0,
      scale: 0.9,
      duration: exitDuration,
      ease: 'power2.in',
      onComplete: () => {
        this.currentIndex = (this.currentIndex + 1) % this.itemCount

        gsap.set(frontItem, {
          y: backPosition.y + 20,
          scale: backPosition.scale,
          zIndex: backPosition.zIndex,
          xPercent: backPosition.xPercent,
        })

        gsap.to(frontItem, {
          y: backPosition.y,
          opacity: backPosition.opacity,
          duration: enterDuration,
          ease: 'power2.out',
          onComplete: () => {
            this.isAnimating = false
            this.scheduleNextRotation()
          },
        })
      },
    })

    this.delayedCallHandle = gsap.delayedCall(exitDuration * 0.3, () => {
      this.animateOtherItems(frontItemIndex, enterDuration)
    })
  }

  getFrontItemIndex() {
    let frontIndex = 0
    this.itemTargets.forEach((_, index) => {
      const positionIndex = (index - this.currentIndex + this.itemCount) % this.itemCount
      if (positionIndex === 0) {
        frontIndex = index
      }
    })
    return frontIndex
  }

  animateOtherItems(excludeIndex, duration) {
    const nextIndex = (this.currentIndex + 1) % this.itemCount

    this.itemTargets.forEach((item, index) => {
      if (index === excludeIndex) return

      const positionIndex = (index - nextIndex + this.itemCount) % this.itemCount
      const position = this.positions[positionIndex]
      if (!position) return

      gsap.to(item, {
        y: position.y,
        scale: position.scale,
        opacity: position.opacity,
        xPercent: position.xPercent,
        duration,
        ease: 'power3.out',
        onStart: () => {
          gsap.set(item, { zIndex: position.zIndex })
        },
      })
    })
  }

  applyPositions() {
    this.itemTargets.forEach((item, index) => {
      const positionIndex = (index - this.currentIndex + this.itemCount) % this.itemCount
      const position = this.positions[positionIndex]
      if (!position) return

      gsap.set(item, {
        y: position.y,
        scale: position.scale,
        zIndex: position.zIndex,
        opacity: position.opacity,
        xPercent: position.xPercent,
      })
    })
  }
}
